import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Chip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { DOCUMENTOS_SOCIO, documentosAprovados, statusDocumento } from "../../../utils/FichaCadastral";
import { botaoPrincipalSx, botaoSecundarioSx, cardSx, iconeSecaoSx, STATUS_DOCUMENTO_CHIP, subtituloSecaoSx, tituloSecaoSx } from "./EstilosFicha";

const TAMANHO_MAXIMO_MB = 10;
const TIPOS_ACEITOS = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

const iniciais = (texto) =>
    String(texto ?? "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

const ChipStatus = ({ status }) => {
    const { label, cor, fundo } = STATUS_DOCUMENTO_CHIP[status] ?? STATUS_DOCUMENTO_CHIP.Pendente;
    return <Chip label={label} size="small" sx={{ backgroundColor: fundo, color: cor, fontWeight: 600, fontSize: 11, height: 20 }} />;
};

// Um documento de um sócio: mostra o status e permite enviar/substituir o arquivo (exceto quando aprovado)
const SlotDocumento = ({ socio, documento, onEnviado, onErro }) => {
    const inputRef = useRef(null);
    const [arquivo, setArquivo] = useState(null);
    const [arrastando, setArrastando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [substituindo, setSubstituindo] = useState(false);

    const status = statusDocumento(socio.documentos, documento.tipo);
    const motivo = socio.documentos?.[`${documento.tipo}MotivoRejeicao`];
    const podeEnviar = status === "Pendente" || status === "Rejeitado" || substituindo;

    const selecionar = (selecionado) => {
        if (!selecionado) return;
        if (!TIPOS_ACEITOS.split(",").includes(selecionado.type)) {
            onErro("Envie uma imagem (JPG, PNG) ou um PDF.");
            return;
        }
        if (selecionado.size > TAMANHO_MAXIMO_MB * 1024 * 1024) {
            onErro(`O arquivo deve ter no máximo ${TAMANHO_MAXIMO_MB} MB.`);
            return;
        }
        setArquivo(selecionado);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setArrastando(false);
        if (!enviando) selecionar(event.dataTransfer.files[0]);
    };

    const handleEnviar = async () => {
        if (!arquivo) return;
        setEnviando(true);

        const formData = new FormData();
        formData.append("tipoDocumento", documento.tipo);
        formData.append("idSocio", socio.idSocio);
        formData.append("file", arquivo);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/uploadDocumentacao`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: localStorage.getItem("token"),
                },
            });
            if (response.data.status === 200) {
                setArquivo(null);
                setSubstituindo(false);
                await onEnviado(`${documento.nome} de ${socio.nome} enviado para análise!`);
            } else {
                onErro(response.data.mensagem || "Erro ao enviar o arquivo.");
            }
        } catch (error) {
            onErro(error.response?.data?.mensagem || "Erro ao enviar o arquivo.");
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", p: 1.5, display: "flex", flexDirection: "column", gap: 1.25, backgroundColor: "#fff" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{documento.nome}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{documento.descricao}</Typography>
                </Box>
                <ChipStatus status={status} />
            </Box>

            {status === "Rejeitado" && (
                <Box sx={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", px: 1.25, py: 0.75 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#b91c1c" }}>Documento rejeitado. Envie novamente.</Typography>
                    {motivo && <Typography sx={{ fontSize: 12, color: "#7f1d1d" }}>Motivo: {motivo}</Typography>}
                </Box>
            )}

            {podeEnviar ? (
                <>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-label={`Selecionar arquivo de ${documento.nome}`}
                        onClick={() => !enviando && inputRef.current?.click()}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !enviando && inputRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
                        onDragLeave={() => setArrastando(false)}
                        sx={{
                            border: `2px dashed ${arrastando ? "#0b243d" : "#ffc845"}`,
                            borderRadius: "8px",
                            backgroundColor: arrastando ? "#fffbeb" : "#fafafa",
                            px: 1.5,
                            py: 2.5,
                            textAlign: "center",
                            cursor: enviando ? "default" : "pointer",
                            transition: "background-color .15s",
                            "&:hover": { backgroundColor: "#fffbeb" },
                        }}
                    >
                        {arquivo ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, minWidth: 0 }}>
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: "#374151", flexShrink: 0 }} />
                                <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{arquivo.name}</Typography>
                            </Box>
                        ) : (
                            <>
                                <CloudUploadOutlinedIcon sx={{ fontSize: 26, color: "#9ca3af" }} />
                                <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Arraste o arquivo ou clique para selecionar</Typography>
                                <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>JPG, PNG ou PDF até {TAMANHO_MAXIMO_MB} MB</Typography>
                            </>
                        )}
                    </Box>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={TIPOS_ACEITOS}
                        hidden
                        onChange={(e) => {
                            selecionar(e.target.files[0]);
                            e.target.value = "";
                        }}
                    />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        {substituindo && (
                            <Button size="small" disabled={enviando} onClick={() => { setSubstituindo(false); setArquivo(null); }} sx={botaoSecundarioSx}>
                                Cancelar
                            </Button>
                        )}
                        <Button size="small" variant="contained" onClick={handleEnviar} disabled={!arquivo} loading={enviando} sx={botaoPrincipalSx}>
                            Enviar
                        </Button>
                    </Box>
                </>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, backgroundColor: "#f9fafb", borderRadius: "8px", px: 1.25, py: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "#374151" }}>
                        {status === "Válido" ? "Documento aprovado pela equipe Clarea." : "Recebido. Nossa equipe está analisando."}
                    </Typography>
                    {status === "Em Análise" && (
                        <Button size="small" onClick={() => setSubstituindo(true)} sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#111827", p: 0, minWidth: 0, "&:hover": { backgroundColor: "transparent", textDecoration: "underline" } }}>
                            Substituir
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

// Etapa 3 da ficha (e página de Documentos): documentos de cada sócio
const DocumentosSocios = ({ socios, onAtualizar, onSucesso, onErro }) => {
    const [expandido, setExpandido] = useState(null);

    // Abre o primeiro sócio com documentos pendentes
    useEffect(() => {
        if (expandido !== null || socios.length === 0) return;
        const primeiroPendente = socios.find((s) => !documentosAprovados(s)) || socios[0];
        setExpandido(primeiroPendente.idSocio);
    }, [socios, expandido]);

    const handleEnviado = async (texto) => {
        await onAtualizar();
        onSucesso(texto);
    };

    const totalAprovados = socios.filter(documentosAprovados).length;

    return (
        <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={iconeSecaoSx}><FolderSharedOutlinedIcon /></Box>
                    <Box>
                        <Typography sx={tituloSecaoSx}>Documentos dos sócios</Typography>
                        <Typography sx={subtituloSecaoSx}>Cada sócio precisa enviar RG ou CNH, título de eleitor e comprovante de residência.</Typography>
                    </Box>
                </Box>
                <Chip
                    label={`${totalAprovados} de ${socios.length} ${socios.length === 1 ? "sócio aprovado" : "sócios aprovados"}`}
                    size="small"
                    sx={{ backgroundColor: totalAprovados === socios.length ? "#dcfce7" : "#f3f4f6", color: totalAprovados === socios.length ? "#15803d" : "#374151", fontWeight: 600, fontSize: 11 }}
                />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {socios.map((socio) => {
                    const status = DOCUMENTOS_SOCIO.map((d) => statusDocumento(socio.documentos, d.tipo));
                    const aprovados = status.filter((s) => s === "Válido").length;
                    const acao = status.filter((s) => s === "Pendente" || s === "Rejeitado").length;

                    return (
                        <Accordion
                            key={socio.idSocio}
                            expanded={expandido === socio.idSocio}
                            onChange={(_, aberto) => setExpandido(aberto ? socio.idSocio : false)}
                            disableGutters
                            elevation={0}
                            sx={{ border: "1px solid #e5e7eb", borderRadius: "10px !important", backgroundColor: "#f9fafb", "&::before": { display: "none" } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                                    <Avatar variant="rounded" sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, color: "#ffc845", backgroundColor: "#0b243d", borderRadius: "6px" }}>
                                        {iniciais(socio.nome)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{socio.nome}</Typography>
                                        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                                            {aprovados} de {DOCUMENTOS_SOCIO.length} aprovados{acao ? ` • ${acao} aguardando envio` : ""}
                                        </Typography>
                                    </Box>
                                    {aprovados === DOCUMENTOS_SOCIO.length && <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#16a34a", mr: 1 }} />}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                                    {DOCUMENTOS_SOCIO.map((documento) => (
                                        <SlotDocumento key={documento.tipo} socio={socio} documento={documento} onEnviado={handleEnviado} onErro={onErro} />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Box>
    );
};

export default DocumentosSocios;
